import { useEffect, useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type {
  GoogleSignInResponse,
  GoogleSignInPendingResponse,
} from '@/types/auth';

// Google Identity Services types - must match Login/index.tsx declaration
interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

// Note: Window.google type is declared in Login/index.tsx to avoid duplicate declarations

const GOOGLE_GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

interface GoogleSignInButtonProps {
  mode: 'signin' | 'signup' | 'link';
  onSuccess: (response: GoogleSignInResponse) => void;
  onNeedCompanyInfo?: (pendingData: GoogleSignInPendingResponse) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  width?: number;
}

// Type guard to check if response needs company info
function isPendingResponse(
  response: GoogleSignInResponse | GoogleSignInPendingResponse
): response is GoogleSignInPendingResponse {
  return 'requires_company_info' in response && response.requires_company_info === true;
}

export function GoogleSignInButton({
  mode,
  onSuccess,
  onNeedCompanyInfo,
  onError,
  disabled = false,
  width = 280,
}: GoogleSignInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const buttonContainerId = `google-signin-button-${mode}`;

  // Get client ID from environment
  const clientId = import.meta.env.VITE_GOOGLE_SIGNIN_CLIENT_ID;

  // Mutation for Google Sign-In/Signup
  const googleSignInMutation = useMutation({
    mutationFn: (idToken: string) =>
      authApi.googleSignIn({
        id_token: idToken,
        signup_intent: mode === 'signup',
      }),
    onSuccess: (response) => {
      setIsLoading(false);
      if (isPendingResponse(response)) {
        // New user needs to provide company info
        if (onNeedCompanyInfo) {
          onNeedCompanyInfo(response);
        } else {
          onError('Company information is required to complete signup');
        }
      } else {
        // Existing user, complete sign-in
        onSuccess(response);
      }
    },
    onError: (error: unknown) => {
      setIsLoading(false);
      const apiError = error as {
        response?: {
          status?: number;
          data?: {
            detail?: string | { code?: string; message?: string };
          };
        };
      };

      // Handle specific error cases
      if (apiError.response?.status === 409) {
        const detail = apiError.response.data?.detail;
        if (typeof detail === 'object' && detail?.code === 'email_exists_no_google_link') {
          onError(detail.message || 'An account with this email already exists. Please sign in with your password.');
        } else {
          onError('An account with this email already exists');
        }
      } else if (apiError.response?.data?.detail) {
        const detail = apiError.response.data.detail;
        onError(typeof detail === 'string' ? detail : 'Google sign-in failed');
      } else {
        onError('Failed to sign in with Google');
      }
    },
  });

  // Mutation for linking Google account
  const googleLinkMutation = useMutation({
    mutationFn: (idToken: string) => authApi.googleLinkAccount({ id_token: idToken }),
    onSuccess: () => {
      setIsLoading(false);
      // For link mode, we don't have a GoogleSignInResponse, but we can create a success indicator
      onSuccess({
        access_token: '',
        refresh_token: '',
        token_type: 'bearer',
        member_uuid: '',
        is_new_user: false,
      });
    },
    onError: (error: unknown) => {
      setIsLoading(false);
      const apiError = error as {
        response?: { data?: { detail?: string } };
      };
      onError(apiError.response?.data?.detail || 'Failed to link Google account');
    },
  });

  // Use ref to store callback - prevents stale closure issues
  // Google caches the callback passed to initialize(), so we use a ref
  // that always points to the latest callback function
  const callbackRef = useRef<((response: GoogleCredentialResponse) => void) | null>(null);

  // Update the ref on every render with the latest callback logic
  callbackRef.current = (response: GoogleCredentialResponse) => {
    if (!response.credential) {
      onError('No credential received from Google');
      return;
    }

    setIsLoading(true);

    if (mode === 'link') {
      googleLinkMutation.mutate(response.credential);
    } else {
      googleSignInMutation.mutate(response.credential);
    }
  };

  // Initialize Google Identity Services
  // Note: callback is intentionally excluded from dependencies. initializeGoogle
  // uses callbackRef.current, so Google always sees the latest callback without
  // re-running this effect when the callback identity changes.
  useEffect(() => {
    if (!clientId) {
      console.warn('Google Sign-In client ID not configured');
      return;
    }

    // Function to initialize Google and render button
    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        // Use wrapper function that calls ref.current - this ensures
        // Google always invokes the latest callback, not a stale one
        callback: (response: GoogleCredentialResponse) => {
          callbackRef.current?.(response);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Clear and render the button
      const buttonContainer = document.getElementById(buttonContainerId);
      if (buttonContainer) {
        // Clear any existing button content before re-rendering
        buttonContainer.innerHTML = '';
        window.google.accounts.id.renderButton(buttonContainer, {
          theme: 'outline',
          size: 'large',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          shape: 'rectangular',
          width: width,
          logo_alignment: 'left',
        });
      }
    };

    // Check if Google script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogle();
      return;
    }

    // Check if script tag already exists (but not yet loaded)
    const existingScript = document.querySelector(
      `script[src="${GOOGLE_GSI_SCRIPT_URL}"]`
    );

    if (existingScript) {
      // Script exists, wait for it to load
      existingScript.addEventListener('load', initializeGoogle);
      return () => {
        existingScript.removeEventListener('load', initializeGoogle);
      };
    }

    // Load the Google Identity Services script
    const script = document.createElement('script');
    script.src = GOOGLE_GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;

    document.body.appendChild(script);

    return () => {
      // Only remove script on unmount if we added it
      script.remove();
    };
  }, [clientId, buttonContainerId, mode, width]);

  // Show warning if client ID is not configured
  if (!clientId) {
    return (
      <div className="text-center text-sm text-gray-500 py-2">
        Google Sign-In is not configured
      </div>
    );
  }

  return (
    <div className="relative" style={{ minHeight: '44px', overflow: 'visible' }}>
      <div
        id={buttonContainerId}
        className={`google-signin-button ${disabled || isLoading ? 'pointer-events-none opacity-50' : ''}`}
        style={{
          minHeight: '44px',
          minWidth: `${width}px`,
          touchAction: 'manipulation', // Prevents touch delay on mobile
          overflow: 'visible',
        }}
      />
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 pointer-events-none">
          <LoadingSpinner size="md" variant="gray" />
        </div>
      )}
    </div>
  );
}
